
"use client";

import { useModal } from "@/app/shared/modal-views/use-modal";
import EyeIcon from "@/components/icons/eye";
import { HeaderCell } from "@/components/ui/table";
import { SortConfig } from "@/hooks/useTableServer";
import {
  EActionTrackingType,
  IEnquiryEntry,
  IEnquiryEntryLogAction,
} from "@/utilities/types/EnquiryEntry";
import dayjs from "dayjs";
import { ActionIcon, Badge, Checkbox, Text, Tooltip } from "rizzui";
import EnquiryEntryDetail from "../details/enquiry-entries-details";
import DeletePopover from "@/app/shared/delete-popover";
import { PiWarningLight, PiWhatsappLogoLight } from "react-icons/pi";
import { IUserFeature } from "@/utilities/types/AdvanceFeature";
import convertToSingaporeTime from "@/utilities/functions/convertToSingaporeTime";
import formatPhone from "@/utilities/functions/format-phone";
import { addSpacesToCamelCase } from "@/utils/add-spaces-to-camel-case";
import EnquiryEntriesRepository from "@/utilities/repositories/EnquiryEntries";
import PhoneIcon from "@/components/icons/phone.tsx";
import { Notes } from "@/app/app-shared/enquiry-entrties/notes";
import { LuNotebookPen } from "react-icons/lu";
import { getScoreColor } from "@/utilities/functions/lead";

type ContactSortBy = "project" | "createdAt" | "name" | "score";

type LogActionSortBy = "createdAt";

export type LogActionColumns = {
  data: IEnquiryEntryLogAction[];
  sortConfig?: SortConfig<LogActionSortBy>;
  onHeaderCellClick?: (value: string) => void;
};

export type Columns = {
  data: IEnquiryEntry[];
  sortConfig?: SortConfig<ContactSortBy>;
  onDeleteItem: (id: string) => void;
  onHeaderCellClick?: (value: string) => void;
  isAdmin: boolean;
  handleSelectAll?: any;
  checkedItems?: string[];
  onChecked?: (rowId: string) => void;
  multipleContact: IUserFeature | undefined;
  onRefresh?: () => void;
  onAgentClick?: (agentId: string) => void;
};

export const getColumns = ({
  data,
  sortConfig,
  onDeleteItem,
  onHeaderCellClick,
  isAdmin,
  checkedItems,
  handleSelectAll,
  onChecked,
  multipleContact,
  onRefresh,
  onAgentClick,
}: Columns) => {
  const columns = [
    {
      title: (
        <HeaderCell
          title={
            <div className="ps-2">
              <Checkbox
                title={""}
                onChange={() => handleSelectAll()}
                checked={checkedItems?.length === data.length}
                className="cursor-pointer"
              />
            </div>
          }
          align="center"
        />
      ),
      align: "center",
      dataIndex: "selectAll",
      key: "selectAll",

      width: 100,
      render: (_: unknown, row: IEnquiryEntry) => {
        return (
          <div className="flex justify-center">
            <Checkbox
              className="ps-2"
              aria-label="Select row"
              defaultValue={row.id}
              checked={checkedItems?.includes(row.id)}
              {...(onChecked && {
                onChange: () => {
                  onChecked(row?.id);
                },
              })}
            />
          </div>
        );
      },
    },
    {
      title: <HeaderCell title={<div></div>} />,
      dataIndex: "spacing",
      key: "spacing",

      width: 10,
      render: () => {
        return <div></div>;
      },
    },
    {
      title: (
        <HeaderCell
          title="Customer"
          sortable
          align="left"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "name"
          }
        />
      ),

      dataIndex: "name",
      key: "name",
      align: "left",
      onHeaderCell: () => onHeaderCellClick?.("name"),

      render: (_: unknown, row: IEnquiryEntry) => {
        return (
          <div>
            <Text className="text-sm font-medium text-gray-900 dark:text-gray-700">
              {row.name}
            </Text>
            {row.email && (
              <Text className="text-[13px] text-gray-500">{row.email}</Text>
            )}
            {row?.phone && (
              <Text className="text-[13px] text-gray-500">
                {formatPhone(row.phone)}
              </Text>
            )}
          </div>
        );
      },
    },

    {
      title: <HeaderCell title="Agent" />,
      dataIndex: "user",
      key: "user",
      render: (_: unknown, row: IEnquiryEntry) => {
        if (!row.user) return;

        const { firstName, lastName, email, phone, id } = row.user;

        const name = firstName + " " + lastName;

        const handleAgentClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          if (id && onAgentClick) {
            onAgentClick(id);
          }
        };

        return (
          <div>
            <Text
              className="text-sm font-medium text-gray-900 dark:text-gray-700 hover:text-blue-600 hover:underline cursor-pointer transition-colors duration-200"
              onClick={handleAgentClick}
            >
              {name}
            </Text>
            {email && (
              <Text className="text-[13px] text-gray-500">{email}</Text>
            )}
            {phone && (
              <Text className="text-[13px] text-gray-500">
                {formatPhone(phone)}
              </Text>
            )}
          </div>
        );
      },
    },

    // {
    //   dataIndex: "leadSource",
    //   key: "leadSource",
    //   title: (
    //     <HeaderCell
    //       title={<span className="whitespace-nowrap">Source</span>}
    //       align="left"
    //     />
    //   ),
    //   render: (_: unknown, row: IEnquiryEntry) => {
    //     if (!row) return;

    //     return (
    //       <div>
    //         <Text className="text-sm">
    //           {getLeadSourceDisplay(row)}
    //         </Text>
    //       </div>
    //     );
    //   },
    // },

    {
      dataIndex: "project",
      key: "project",
      title: (
        <HeaderCell
          title={<span className="whitespace-nowrap">Project</span>}
          sortable
          align="left"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "project"
          }
        />
      ),
      onHeaderCell: () => onHeaderCellClick?.("project"),
      render: (_: unknown, row: IEnquiryEntry) => {
        if (!row.user) return;

        return (
          <div>
            <Text className="text-sm font-medium text-gray-900 dark:text-gray-700">
              {typeof row?.project?.name === "object"
                ? row?.project?.name?.en
                : row?.project?.name}
            </Text>
            {row?.project?.address?.en && (
              <Text className="text-[13px] text-gray-500">
                {row?.project?.address?.en}
              </Text>
            )}
          </div>
        );
      },
    },

    {
      dataIndex: "score",
      key: "score",
      title: (
        <HeaderCell
          title={<span className="whitespace-nowrap">Score</span>}
          sortable
          align="left"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "score"
          }
        />
      ),
      onHeaderCell: () => onHeaderCellClick?.("score"),
      width: 120,
      render: (_: unknown, row: IEnquiryEntry) => {
        if (!row.user) return;

        return (
          <div>
            <Text
              className="text-sm flex items-center justify-start font-medium text-gray-900 dark:text-gray-700"
              style={{ color: getScoreColor(row?.score) }}
            >
              {row?.score}
              {row?.score === 0 && <PiWarningLight className="h-4 w-4" />}
            </Text>
          </div>
        );
      },
    },

    {
      title: (
        <HeaderCell
          title={<HeaderCell title="Submit At" />}
          sortable
          align="left"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "createdAt"
          }
        />
      ),
      onHeaderCell: () => onHeaderCellClick?.("createdAt"),
      align: "left",
      dataIndex: "createdAt",
      key: "createdAt",

      width: 250,
      render: (_: unknown, row: IEnquiryEntry) => {
        if (!dayjs(row?.createdAt).isValid()) return;
        return <div>{convertToSingaporeTime(new Date(row.createdAt))}</div>;
      },
    },

    {
      title: (
        <HeaderCell
          className=""
          align="left"
          title={<span className="whitespace-nowrap">Actions</span>}
        />
      ),
      dataIndex: "action",
      key: "action",
      width: 120,
      align: "left",
      render: (_: string, row: IEnquiryEntry) => {
        return (
          <RenderAction
            onDelete={onDeleteItem}
            row={row}
            isAdmin={isAdmin}
            onRefresh={onRefresh}
          />
        );
      },
    },
  ];

  let filterdColumns = isAdmin
    ? columns
    : columns.filter((column) => column.key !== "user");

  filterdColumns =
    multipleContact || isAdmin
      ? filterdColumns.filter((column) => column.key !== "spacing")
      : filterdColumns.filter((column) => column.key !== "selectAll");

  return filterdColumns;
};

const RenderAction = ({
  onDelete,
  row,
  isAdmin,
  onRefresh,
}: {
  onDelete: (id: string) => void;
  row: IEnquiryEntry;
  isAdmin?: boolean;
  onRefresh?: () => void;
}) => {
  const { openModal } = useModal();
  return (
    <div
      className="flex items-center justify-start gap-3"
      onClick={(e) => e.stopPropagation()}
    >
      <Tooltip size="sm" content={"Call"} placement="top" color="invert">
        <ActionIcon
          size="sm"
          variant="outline"
          onClick={() => {
            window.location.href = `tel:${row.phone}`;
          }}
        >
          <PhoneIcon className="w-4 h-4" />
        </ActionIcon>
      </Tooltip>
      <Tooltip size="sm" content={"WhatsApp"} placement="top" color="invert">
        <ActionIcon
          size="sm"
          variant="outline"
          onClick={async () => {
            window.open(`https://wa.me/${row.phone}`, "_blank");
          }}
        >
          <PiWhatsappLogoLight className="w-5 h-5" />
        </ActionIcon>
      </Tooltip>
      <Tooltip size="sm" content={"View Notes"} placement="top" color="invert">
        <ActionIcon
          size="sm"
          variant="outline"
          aria-label={"View Notes"}
          onClick={async () => {
            openModal({
              view: (
                <Notes
                  data={row}
                  isModalView
                  onRefresh={onRefresh}
                  isAdmin={isAdmin}
                />
              ),
              customSize: "1000px",
              size: "xl",
            });
          }}
        >
          <LuNotebookPen />
        </ActionIcon>
      </Tooltip>
      <Tooltip
        size="sm"
        content={"View Details"}
        placement="top"
        color="invert"
      >
        <ActionIcon
          as="button"
          size="sm"
          variant="outline"
          aria-label={"View Details"}
          className="hover:!border-gray-900 hover:text-gray-700"
          onClick={async () => {
            await EnquiryEntriesRepository.createLogAction({
              submissionId: row.id,
              trackingType: EActionTrackingType.VIEW_DETAIL,
            });

            openModal({
              view: (
                <EnquiryEntryDetail
                  data={row}
                  isModalView
                  onRefresh={onRefresh}
                  isAdmin={isAdmin}
                />
              ),
              customSize: "1000px",
              size: "xl",
            });
          }}
        >
          <EyeIcon className="h-4 w-4" />
        </ActionIcon>
      </Tooltip>
      {isAdmin && (
        <DeletePopover
          title={`Delete Enquiry`}
          description={`Are you sure you want to delete this enquiry?`}
          onDelete={() => onDelete(row.id)}
        />
      )}
    </div>
  );
};

export const getLogActionColumns = ({
  data,
  sortConfig,
  onHeaderCellClick,
}: LogActionColumns) => {
  return [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (_: unknown, row: IEnquiryEntryLogAction) => {
        if (!row?.user) return;
        return (
          <div className="text-sm font-medium text-gray-900 capitalize">
            {row?.user?.firstName} {row?.user?.lastName}{" "}
            <Badge size="sm">{row?.user?.role}</Badge>
          </div>
        );
      },
    },

    {
      title: "Action Type",
      dataIndex: "trackingType",
      key: "trackingType",
      render: (_: unknown, row: IEnquiryEntryLogAction) => {
        return (
          <div className="text-sm font-medium text-gray-900 capitalize">
            {addSpacesToCamelCase(row?.actionTrackingType)}
          </div>
        );
      },
    },

    {
      title: "Comment",
      dataIndex: "content",
      key: "content",
      render: (_: unknown, row: IEnquiryEntryLogAction) => {
        return <div>{row?.content}</div>;
      },
    },

    {
      title: "Action At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (_: unknown, row: IEnquiryEntryLogAction) => {
        // return (
        //   <div>{dayjs(row?.createdAt).format("DD MMM YYYY HH:mm:ss A")}</div>
        // );
        if (!dayjs(row?.createdAt).isValid()) return;
        return <div>{convertToSingaporeTime(new Date(row.createdAt))}</div>;
      },
    },
  ];
};
